import React, { useState, useMemo } from 'react';
import Map, {
  Marker,
  MapConstant,
  Direction,
  MapControls
} from '../../../../../lib/Map';
import DrawingTools from '../../../Settings/common/DrawTools/DrawingTools';
import DrawStyles from '../../../../../lib/Map/Trimble/components/DrawStyles';
import { withStyles, withTheme } from '@material-ui/core';
import { compose } from 'recompose';
import { TerminalMarker } from '../../../../mapComponents/Markers';
import appConstants from '@appConstants';
import { withAppConfig } from '../../../../../appConfigContext';
import {
  getDispatchDriverMarker,
  getDispatchLastStop,
  getDispatchWayPoints
} from '../../../../mapComponents/mapHelper';
import { getRouteStopList, isDriverDelayed, renderStopMarker } from '../Utils';
import MapLegend from './MapLegend';
import EventEmitter from '../../../../../utilities/EventEmitter';
import TerminalGeofenceEntryExitMarker from '../Common/TerminalGeofenceEntryExitMarker';
import { useTerminalGeofence } from './useTerminalGeofence';
import MapHeader from './MapHeader';
import GpsPings from '../../../../common/GpsPings';

const styles = (theme) => ({
  routeMap: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column'
  }
});

const RouteMap = ({ theme, classes, route, configJSON, glEventHub, enqueueSnackbar }) => {
  const [showActualRoute, setShowActualRoute] = useState(false); 
  const [showMapLegend, setShowMapLegend] = useState(true);
  const [showPlannedRoute, setShowPlannedRoute] = useState(true);
  const [styleName, setStyleName] = useState(null);
  const terminalGeoFenceData = useTerminalGeofence(route?.terminal?.terminalId);
  const routeStops = getRouteStopList(route);
  const routeCompletedStops = routeStops.filter((stop) => stop.isStopCompleted);
  const routePendingStops = routeStops.filter((stop) => !stop.isStopCompleted);
  const routeTerminal = route.terminal;
  const location = routeTerminal
    ? {
        lat: routeTerminal.latitude,
        lng: routeTerminal.longitude
      }
    : null;
  const completedStopColors = {
    color: theme.HeaderSelectedText,
    textColor: theme.selectedStopMarkerColor
  };
  const pendingStopColors = {
    color: theme.selectedStopMarkerColor,
    textColor: theme.HeaderSelectedText
  };

  const toggleShowActualRoute = () => {
    setShowActualRoute((prev) => !prev);
  };

  const toggleShowMapLegend = () => {
    setShowMapLegend((prev) => !prev);
  };

  const toggleShowPlannedRoute = () => {
    setShowPlannedRoute((prev) => !prev);
  };

  const terminalGeofenceCoordinates = useMemo(() => {
    if (terminalGeoFenceData.length > 0) {
      return {
        lat: terminalGeoFenceData[0].geometry.coordinates[0][0][1], // use the first coordinate of the terminal geofence polygon boundary
        lng: terminalGeoFenceData[0].geometry.coordinates[0][0][0]
      };
    }
    return null;
  }, [terminalGeoFenceData]);

  const getMapStyleName = (styleName) => {
    if (styleName !== styleName) {
      setStyleName(styleName);
    }
  };

  return (
    <div className={classes.routeMap}>
      <MapHeader
        showActualRoute={showActualRoute}
        classes={classes}
        toggleShowActualRoute={toggleShowActualRoute}
        enqueueSnackbar={enqueueSnackbar}
        toggleShowMapLegend={toggleShowMapLegend}
        showMapLegend={showMapLegend}
        showPlannedRoute={showPlannedRoute}
        toggleShowPlannedRoute={toggleShowPlannedRoute}
        getMapStyleName={getMapStyleName}
      />
      <Map
        zoom={appConstants.MAP_ZOOM_LEVEL}
        initialCenter={location}
        center={location}
        mapId={appConstants.mapIds.RECORDS_LOOKUP_ROUTE_MAP}
        mapTheme={theme.themeName}
        eventHub={glEventHub}
        mapConfig={configJSON?.maps}
        onClick={null}>
        <DrawingTools
          polygonOptions={{
            fillColor: theme.MapDrawingColor,
            strokeColor: theme.MapDrawingColor
          }}
          geoDataList={terminalGeoFenceData}
          styleArray={DrawStyles(theme.commonAppHeaderTabColor)}
          eventHub={EventEmitter}
          featureIds={route?.terminal?.terminalId ? [route.terminal.terminalId] : []}
        />
        {terminalGeofenceCoordinates && 
          <TerminalGeofenceEntryExitMarker
            entry={{
              ...terminalGeofenceCoordinates,
              time: route.terminalGeofenceEntryTime
            }}
            exit={{
              ...terminalGeofenceCoordinates,
              time: route.terminalGeofenceExitTime
            }}
            theme={theme}
            key={route.manifestNumber}
          />
        }
        <MapControls
          controlOptions={{
            zoomControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            },
            satelliteViewControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            },
            reCenterMapControl: {
              position: MapConstant.CONTROL_POSITION.BOTTOM_RIGHT
            }
          }}
        />
        {routeTerminal ? (
          <Marker
            position={location}
            zIndex={2}
            forceUpdate
            isActiveShipment
            easeToAnimation
            mapZoomLevel={appConstants.MAP_ZOOM_LEVEL}>
            <TerminalMarker
              data={{ ...routeTerminal, name: routeTerminal.terminalName }}
              color={theme.terminalContentColor}
            />
          </Marker>
        ) : null}

        {route.isLiveDispatch && route.lastKnownLongitude && route.lastKnownLatitude && (
          <Marker
            position={{ lat: route.lastKnownLatitude, lng: route.lastKnownLongitude }}
            zIndex={1}
            key={route.driverId}
            data={route}
            onClick={null}>
            {getDispatchDriverMarker({
              data: route,
              isDelayed: isDriverDelayed(route),
              showDriverLabel: true,
              theme,
              onMessageIconClick: null
            })}
          </Marker>
        )}

        {showPlannedRoute && routeCompletedStops.length > 0 ? (
          <Direction
            refreshOnMapStyleLoaded
            origin={location}
            destination={
              route.isLiveDispatch && route.lastKnownLongitude && route.lastKnownLatitude
                ? { lat: route.lastKnownLatitude, lng: route.lastKnownLongitude }
                : getDispatchLastStop(routeCompletedStops)
            }
            waypoints={[
              ...getDispatchWayPoints(routeCompletedStops),
              ...(route.isLiveDispatch && route.lastKnownLongitude && route.lastKnownLatitude
                ? [{ location: { lat: route.lastKnownLatitude, lng: route.lastKnownLongitude } }]
                : [])
            ]}
            strokeColor={theme.HeaderSelectedText}
            data={{ routeId: `${route.routeId}-completedStops`, color: theme.HeaderSelectedText }}
            key={`${route.routeId}-RecordLookup-completedStops`}
            zIndex={1}
            routeAnimation={true}
          />
        ) : null}

        {showPlannedRoute && route.isLiveDispatch && routePendingStops.length > 0 ? (
          <Direction
            refreshOnMapStyleLoaded
            origin={
              route.lastKnownLongitude && route.lastKnownLatitude
                ? { lat: route.lastKnownLatitude, lng: route.lastKnownLongitude }
                : location
            }
            destination={getDispatchLastStop(routePendingStops)}
            waypoints={getDispatchWayPoints(routePendingStops)}
            strokeColor={theme.HeaderSelectedText}
            data={{ routeId: `${route.routeId}-pendingStop`, color: theme.HeaderSelectedText }}
            key={`${route.routeId}-RecordLookup-pendingStop`}
            zIndex={1}
            routeAnimation={true}
            pathType={appConstants.DOTTED_ROUTE}
          />
        ) : null}

        {configJSON?.enableDriverActualRoute && (
          <GpsPings
            route={route}
            theme={theme}
            showActualRoute={showActualRoute}
            styleName={styleName}
          />
        )}
        {route?.stops?.map((stop) => (
          <Marker
            data={{ ...stop }}
            position={{
              lat: stop.consigneeDetails.latitude,
              lng: stop.consigneeDetails.longitude
            }}
            key={stop.stopId}
            zIndex={1}
            onClick={null}>
            {renderStopMarker(stop, stop.isStopCompleted ? completedStopColors : pendingStopColors)}
          </Marker>
        ))}
        {showMapLegend && (
          <MapLegend theme={theme} enableDriverActualRoute={configJSON?.enableDriverActualRoute} />
        )}
      </Map>
    </div>
  );
};

export default compose(withStyles(styles), withTheme(), withAppConfig)(RouteMap);
